import { useState, useEffect } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Card, CardContent, Input } from '@pantry/ui';
import { useHouseholds, useCreateHousehold, useResetTour, OFF_COUNTRIES } from '@pantry/api-client';
import {
  ArchiveBoxIcon,
  Cog6ToothIcon,
  HomeIcon,
  PlusIcon,
  UserGroupIcon,
  GlobeAltIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { useTourContext } from '../../components/onboarding';

export const Route = createFileRoute('/_app/households')({
  component: HouseholdsPage,
});

function HouseholdsPage() {
  const { t } = useLingui();
  const { data: households, isLoading, error } = useHouseholds();
  const createHousehold = useCreateHousehold();
  const resetTour = useResetTour();
  const { setFirstHouseholdId, startTour } = useTourContext();

  // Update the tour context with the first household ID for navigation
  useEffect(() => {
    if (households && households.length > 0) {
      setFirstHouseholdId(households[0].id);
    }
  }, [households, setFirstHouseholdId]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [newHouseholdCountry, setNewHouseholdCountry] = useState('world');
  const [isStartingHouseholdsMiniTour, setIsStartingHouseholdsMiniTour] = useState(false);

  const handleStartHouseholdsMiniTour = async () => {
    if (isStartingHouseholdsMiniTour) return;

    setIsStartingHouseholdsMiniTour(true);
    try {
      await resetTour.mutateAsync({ tourId: 'households-basics', deviceType: 'web' });
      await startTour('households-basics');
    } catch (tourError) {
      console.error('Failed to start households mini tour:', tourError);
    } finally {
      setIsStartingHouseholdsMiniTour(false);
    }
  };

  const handleCreateHousehold = async () => {
    if (!newHouseholdName.trim()) return;
    await createHousehold.mutateAsync({ name: newHouseholdName, country: newHouseholdCountry });
    setNewHouseholdName('');
    setNewHouseholdCountry('world');
    setShowCreateForm(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-destructive animate-in fade-in duration-200">
        <Trans>Failed to load households: {error.message}</Trans>
      </div>
    );
  }

  const hasHouseholds = households && households.length > 0;

  return (
    <div className="space-y-6" data-tour="households-page">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <HomeIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans>Households</Trans>
            </h1>
            <p className="text-muted-foreground">
              <Trans>Manage your households and members.</Trans>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowCreateForm(true)}
            className="rounded-xl h-10"
            data-tour="create-household"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            <Trans>New Household</Trans>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => void handleStartHouseholdsMiniTour()}
            data-tour="households-help-basics"
            aria-label={t`Start households basics mini tour`}
            title={t`Households basics help`}
            aria-busy={isStartingHouseholdsMiniTour}
            disabled={isStartingHouseholdsMiniTour}
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <Card className="rounded-xl animate-in fade-in slide-in-from-top-2 duration-200">
          <CardContent className="py-4">
            <div className="space-y-3">
              <Input
                type="text"
                placeholder={t`Household name`}
                value={newHouseholdName}
                onChange={(e) => setNewHouseholdName(e.target.value)}
                className="rounded-xl"
                autoFocus
                data-tour="household-name-input"
              />
              <select
                value={newHouseholdCountry}
                onChange={(e) => setNewHouseholdCountry(e.target.value)}
                className="rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm h-10 w-full"
                aria-label={t`Region`}
              >
                {OFF_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              <div className="flex gap-3">
                <Button
                  onClick={handleCreateHousehold}
                  disabled={createHousehold.isPending || !newHouseholdName.trim()}
                  className="rounded-xl"
                >
                  {createHousehold.isPending ? <Trans>Creating...</Trans> : <Trans>Create</Trans>}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateForm(false)}
                  className="rounded-xl"
                >
                  <XMarkIcon className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {hasHouseholds ? (
        <div className="grid gap-4">
          {households?.map((household, index) => {
            const isFirstHousehold = index === 0;
            return (
              <Card
                key={household.id}
                className="rounded-xl hover:shadow-md transition-all duration-200 animate-in fade-in slide-in-from-bottom-2"
                style={{ animationDelay: `${index * 50}ms` }}
                data-tour={isFirstHousehold ? 'household-card' : undefined}
                data-household-id={household.id}
              >
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
                        <HomeIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-foreground">
                          {household.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <UserGroupIcon className="h-4 w-4" />
                          <span>
                            <Plural
                              value={household.members.length}
                              one="# member"
                              other="# members"
                            />
                          </span>
                          {household.country && household.country !== 'world' && (
                            <>
                              <span className="text-border">•</span>
                              <GlobeAltIcon className="h-4 w-4" />
                              <span>{household.country.toUpperCase()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="rounded-xl" asChild>
                        <Link
                          to="/households/$householdId/storage-locations"
                          params={{ householdId: household.id }}
                        >
                          <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                          <Trans>Storage</Trans>
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-xl" asChild>
                        <Link
                          to="/households/$householdId/product-groups"
                          params={{ householdId: household.id }}
                        >
                          <RectangleStackIcon className="h-4 w-4 mr-2" />
                          <Trans context="nav_link">Groups</Trans>
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                        asChild
                        data-tour={isFirstHousehold ? 'household-settings-btn' : undefined}
                      >
                        <Link to="/households/$householdId" params={{ householdId: household.id }}>
                          <Cog6ToothIcon className="h-4 w-4 mr-2" />
                          <Trans>Settings</Trans>
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="rounded-2xl animate-in fade-in duration-300">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                <HomeIcon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-display text-xl font-semibold text-foreground">
                <Trans>No households yet</Trans>
              </h3>
              <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
                <Trans>
                  Create your first household to start tracking your pantry and sharing with family.
                </Trans>
              </p>
              <Button className="mt-6 rounded-xl h-11" onClick={() => setShowCreateForm(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                <Trans>Create Household</Trans>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
